import React, { useState, useEffect } from 'react'
import 'katex/dist/katex.min.css'
import katex from 'katex'
import { ContentItem } from '../../types'
import AIButton from '../ai/AIButton'
import KaTeXRenderer from '../common/KaTeXRenderer'
import { imageExtractor } from '../../services/teacher/ImageExtractor'
import API_CONFIG from '../../config/api.config'

interface ContentRendererProps {
  content: ContentItem[]
  onError?: (error: Error, item: ContentItem) => void
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ content, onError }) => {
  // Re-initialize YouTube embeds when content changes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        ; (window as any).forceYouTubeEmbedInit?.()
          ; (window as any).youtubeCspBypassInit?.()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [content])

  // Helper function to process markdown and formulas (both inline and block)
  const processTextContent = (text: string): string => {
    let processedText = text

    // ✅ Process block formulas first: $$...$$ (display mode)
    processedText = processedText.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula.trim(), {
          displayMode: true,
          throwOnError: false,
          errorColor: '#cc0000'
        })
      } catch (error) {
        const katexError = error as Error
        console.warn('Lỗi KaTeX block:', katexError.message, 'Formula:', formula)
        return `<div class="text-gray-700 bg-gray-100 px-2 py-1 rounded text-sm font-mono my-2" title="Công thức: ${formula}">${formula}</div>`
      }
    })

    // ✅ Process inline formulas: $...$ (inline mode)
    processedText = processedText.replace(/\$(.*?)\$/g, (match, formula) => {
      try {
        return katex.renderToString(formula, {
          displayMode: false,
          throwOnError: false,
          errorColor: '#cc0000'
        })
      } catch (error) {
        const katexError = error as Error
        console.warn('Lỗi KaTeX inline:', katexError.message, 'Formula:', formula)
        return `<span class="text-gray-700 bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" title="Công thức: ${formula}">${formula}</span>`
      }
    })

    // Process markdown bold: **text** → <strong class="...">text</strong>
    // Brilliant.org style: bold with slightly darker color for emphasis
    processedText = processedText.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-blue-700">$1</strong>')

    // ✅ Process newlines: \n → <br/> (convert newlines to HTML line breaks)
    processedText = processedText.replace(/\n/g, '<br/>')

    return processedText
  }

  const renderContentItem = (item: ContentItem, index: number) => {
    try {
      switch (item.type) {
        case 'text':
          // Xử lý markdown và công thức inline trong text
          const textContent = item.content as string
          const processedTextContent = processTextContent(textContent)

          // Nếu có xử lý (formulas hoặc markdown), dùng dangerouslySetInnerHTML
          if (processedTextContent !== textContent) {
            return (
              <div key={index} className="group relative">
                <p className="text-gray-800 leading-tight mb-1.5 sm:mb-3 text-sm sm:text-base">
                  <span dangerouslySetInnerHTML={{ __html: processedTextContent }} />
                </p>
                {/* AIButton removed for paragraph content to reduce clutter */}
              </div>
            )
          }

          // Nếu không có xử lý đặc biệt, render text thuần
          return (
            <div key={index} className="group relative">
              <p className="text-gray-800 leading-tight mb-1.5 sm:mb-3 text-sm sm:text-base">
                {textContent}
              </p>
              {/* AIButton removed for text content to reduce clutter */}
            </div>
          )

        case 'heading':
          // Brilliant.org style: bold, vibrant colors - Minimal Spacing
          const headingClass = item.level === 3
            ? 'text-sm sm:text-base font-bold text-blue-700 mb-1 sm:mb-2 mt-2 sm:mt-4 leading-tight'
            : item.level === 2
              ? 'text-base sm:text-xl font-bold text-blue-900 mb-1.5 sm:mb-3 mt-3 sm:mt-6 pb-1 border-b-2 border-blue-200 leading-tight'
              : 'text-base sm:text-lg font-bold text-indigo-900 mb-1.5 sm:mb-3 mt-2 sm:mt-4 leading-tight'
          return (
            <h3 key={index} className={headingClass}>
              {item.content as string}
            </h3>
          )

        case 'list':
          if (!item.items || !Array.isArray(item.items)) {
            return (
              <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                <p className="text-yellow-800 text-sm">Danh sách không có nội dung</p>
              </div>
            )
          }

          if (item.style === 'numbered') {
            return (
              <ol key={index} className="list-decimal list-outside space-y-1 sm:space-y-2 mb-2 sm:mb-4 ml-4 sm:ml-5 pl-0.5 sm:pl-1">
                {item.items.map((listItem, listIndex) => {
                  const processedItem = processTextContent(listItem)
                  return (
                    <li key={listIndex} className="text-gray-800 leading-tight pl-0.5 sm:pl-1 text-sm sm:text-base">
                      <span dangerouslySetInnerHTML={{ __html: processedItem }} />
                    </li>
                  )
                })}
              </ol>
            )
          } else {
            return (
              <ul key={index} className="space-y-1 sm:space-y-2 mb-2 sm:mb-4 ml-0.5 sm:ml-1">
                {item.items.map((listItem, listIndex) => {
                  const processedItem = processTextContent(listItem)
                  return (
                    <li key={listIndex} className="flex items-start text-gray-800 leading-tight text-sm sm:text-base">
                      <span className="text-blue-600 mr-1.5 sm:mr-2 mt-0.5 sm:mt-1 flex-shrink-0">●</span>
                      <span dangerouslySetInnerHTML={{ __html: processedItem }} />
                    </li>
                  )
                })}
              </ul>
            )
          }

        case 'formula':
          try {
            // Support both old format (item.content) and new format (item.formula)
            const formulaContent = item.formula || item.content

            // If no formula content, skip rendering (don't create empty box)
            if (!formulaContent) {
              console.warn('Formula item without formula or content field, skipping:', item)
              return null
            }

            const katexHTML = katex.renderToString(formulaContent as string, {
              displayMode: true,
              throwOnError: false,
              errorColor: '#cc0000'
            })

            return (
              <div key={index} className="my-2 sm:my-4 p-2 sm:p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded group relative">
                {/* Title if exists */}
                {item.title && (
                  <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base leading-tight">
                    {item.title}
                  </h4>
                )}

                {/* Formula */}
                <div
                  className="text-center mb-1 overflow-x-auto text-blue-700 text-sm sm:text-base"
                  dangerouslySetInnerHTML={{ __html: katexHTML }}
                />

                {/* Description */}
                {item.description && (
                  <p className="text-[0.7rem] sm:text-xs text-blue-800 text-center font-medium mt-1 sm:mt-2 italic leading-tight">
                    {item.description}
                  </p>
                )}

                {/* Variables explanation */}
                {item.variables && Array.isArray(item.variables) && item.variables.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <div className="text-[0.65rem] sm:text-xs text-blue-700 space-y-0.5">
                      {item.variables.map((v: any, vIndex: number) => (
                        <div key={vIndex} className="flex items-start gap-1">
                          <span className="font-mono font-semibold">{v.symbol}:</span>
                          <span>{v.meaning}</span>
                          {v.unit && <span className="text-blue-600">({v.unit})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Old explanation field for backward compatibility */}
                {item.explanation && (
                  <p className="text-[0.7rem] sm:text-xs text-blue-800 text-center font-medium mt-1 sm:mt-2 italic leading-tight">
                    {item.explanation}
                  </p>
                )}
              </div>
            )
          } catch (error) {
            const katexError = error as Error
            const formulaContent = item.formula || item.content
            console.warn('KaTeX render warning:', katexError.message, 'Formula:', formulaContent)

            // If no formula content, don't create empty box
            if (!formulaContent) {
              return null
            }

            // Hiển thị fallback với công thức gốc
            return (
              <div key={index} className="my-6 p-4 bg-gray-50 border border-gray-200 rounded-lg group relative">
                {item.title && (
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h4>
                )}
                <div className="text-center mb-2 font-mono text-gray-700 bg-white p-3 rounded border">
                  {formulaContent as string}
                </div>
                {item.description && (
                  <p className="text-sm text-gray-600 text-center italic">
                    {item.description}
                  </p>
                )}
                {item.explanation && (
                  <p className="text-sm text-gray-600 text-center italic">
                    {item.explanation}
                  </p>
                )}
                <p className="text-xs text-gray-500 text-center mt-2">
                  (Công thức hiển thị dạng text)
                </p>
              </div>
            )
          }

        case 'section':
          return (
            <div key={index} className="mb-6">
              {item.title && (
                <h4 className="text-lg font-semibold text-gray-800 mb-3">
                  {item.title}
                </h4>
              )}
              {Array.isArray(item.content) && (
                <div className="space-y-3">
                  {item.content.map((subItem, subIndex) =>
                    renderContentItem(subItem, Number(`${index}${subIndex}`))
                  )}
                </div>
              )}
            </div>
          )

        case 'formula_card': {
          const { color = 'blue', icon = '📐', formula = '', description = '', content: extraContent } = item as any

          const cardColorSchemes: Record<string, { bg: string; border: string; title: string; innerBorder: string }> = {
            blue: { bg: 'bg-blue-50', border: 'border-blue-200', title: 'text-blue-900', innerBorder: 'border-blue-300' },
            green: { bg: 'bg-green-50', border: 'border-green-200', title: 'text-green-900', innerBorder: 'border-green-300' },
            yellow: { bg: 'bg-yellow-50', border: 'border-yellow-300', title: 'text-yellow-900', innerBorder: 'border-yellow-400' },
            orange: { bg: 'bg-orange-50', border: 'border-orange-200', title: 'text-orange-900', innerBorder: 'border-orange-300' },
            red: { bg: 'bg-red-50', border: 'border-red-200', title: 'text-red-900', innerBorder: 'border-red-300' },
            purple: { bg: 'bg-purple-50', border: 'border-purple-200', title: 'text-purple-900', innerBorder: 'border-purple-300' },
            indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', title: 'text-indigo-900', innerBorder: 'border-indigo-300' },
            pink: { bg: 'bg-pink-50', border: 'border-pink-200', title: 'text-pink-900', innerBorder: 'border-pink-300' },
            teal: { bg: 'bg-teal-50', border: 'border-teal-200', title: 'text-blue-700', innerBorder: 'border-teal-300' },
            gold: { bg: 'bg-amber-50', border: 'border-amber-300', title: 'text-blue-700', innerBorder: 'border-amber-400' }
          }

          const cardColorScheme = cardColorSchemes[color] || cardColorSchemes.blue

          const descriptionText = description || extraContent || ''

          return (
            <div key={index} className={`my-2 sm:my-4 p-2 sm:p-3 ${cardColorScheme.bg} border ${cardColorScheme.border} rounded`}>
              <h4 className={`font-bold ${cardColorScheme.title} mb-1 sm:mb-2 flex items-center text-xs sm:text-sm leading-tight`}>
                <span className="text-base sm:text-lg mr-1">{icon}</span>
                {item.title || 'Công thức'}
              </h4>
              <div className={`bg-white p-2 sm:p-3 rounded border ${cardColorScheme.innerBorder}`}>
                {formula ? (
                  <KaTeXRenderer content={formula} displayMode className="my-2 text-base sm:text-lg" />
                ) : null}
                {descriptionText ? (
                  <div className="text-gray-800 leading-tight text-sm sm:text-base">
                    <KaTeXRenderer content={descriptionText} />
                  </div>
                ) : null}
              </div>
            </div>
          )
        }

        case 'concept_card':
        case 'conclusion_card':
        case 'data_table':
          // Render these card types similar to 'example'
          const cardContent = item.content as string
          const processedCardContent = processTextContent(cardContent)

          // Reuse the same color schemes from example
          const cardColorSchemes: Record<string, { bg: string; border: string; title: string; innerBorder: string }> = {
            blue: { bg: 'bg-blue-50', border: 'border-blue-200', title: 'text-blue-900', innerBorder: 'border-blue-300' },
            green: { bg: 'bg-green-50', border: 'border-green-200', title: 'text-green-900', innerBorder: 'border-green-300' },
            yellow: { bg: 'bg-yellow-50', border: 'border-yellow-300', title: 'text-yellow-900', innerBorder: 'border-yellow-400' },
            orange: { bg: 'bg-orange-50', border: 'border-orange-200', title: 'text-orange-900', innerBorder: 'border-orange-300' },
            red: { bg: 'bg-red-50', border: 'border-red-200', title: 'text-red-900', innerBorder: 'border-red-300' },
            purple: { bg: 'bg-purple-50', border: 'border-purple-200', title: 'text-purple-900', innerBorder: 'border-purple-300' },
            indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', title: 'text-indigo-900', innerBorder: 'border-indigo-300' },
            pink: { bg: 'bg-pink-50', border: 'border-pink-200', title: 'text-pink-900', innerBorder: 'border-pink-300' },
            teal: { bg: 'bg-teal-50', border: 'border-teal-200', title: 'text-blue-700', innerBorder: 'border-teal-300' },
            gold: { bg: 'bg-amber-50', border: 'border-amber-300', title: 'text-blue-700', innerBorder: 'border-amber-400' }
          }

          const cardColorScheme = cardColorSchemes[(item as any).color || 'blue'] || cardColorSchemes.blue
          const cardIcon = (item as any).icon || '📌'

          return (
            <div key={index} className={`my-2 sm:my-4 p-2 sm:p-3 ${cardColorScheme.bg} border ${cardColorScheme.border} rounded`}>
              <h4 className={`font-bold ${cardColorScheme.title} mb-1 sm:mb-2 flex items-center text-xs sm:text-sm leading-tight`}>
                <span className="text-base sm:text-lg mr-1">{cardIcon}</span>
                {item.title || 'Nội dung'}
              </h4>
              <div className={`bg-white p-2 sm:p-3 rounded border ${cardColorScheme.innerBorder}`}>
                <div className="text-gray-800 leading-tight text-sm sm:text-base">
                  <span dangerouslySetInnerHTML={{ __html: processedCardContent }} />
                </div>
              </div>
            </div>
          )

        case 'exercise':
          return (
            <div key={index} className="my-6 p-4 bg-blue-50 border border-blue-200 rounded-lg group relative">
              <h4 className="font-semibold text-blue-800 mb-3">
                {item.title || 'Bài tập'}
              </h4>
              {item.problem && (
                <p className="text-gray-700 mb-3">
                  <strong>Đề bài:</strong> {item.problem}
                </p>
              )}
              {item.difficulty && (
                <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                  Độ khó: {item.difficulty}
                </span>
              )}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <AIButton
                  content={item}
                  contentType="exercise"
                  size="sm"
                  variant="outline"
                  title="Giải thích bài tập"
                />
              </div>
            </div>
          )

        case 'example':
          const exampleContent = item.content as string
          const processedExampleContent = processTextContent(exampleContent)

          // Brilliant.org inspired color scheme
          const colorSchemes: Record<string, { bg: string; border: string; title: string; innerBorder: string }> = {
            blue: {
              bg: 'bg-blue-50',
              border: 'border-blue-200',
              title: 'text-blue-900',
              innerBorder: 'border-blue-300'
            },
            green: {
              bg: 'bg-green-50',
              border: 'border-green-200',
              title: 'text-green-900',
              innerBorder: 'border-green-300'
            },
            yellow: {
              bg: 'bg-yellow-50',
              border: 'border-yellow-300',
              title: 'text-yellow-900',
              innerBorder: 'border-yellow-400'
            },
            orange: {
              bg: 'bg-orange-50',
              border: 'border-orange-200',
              title: 'text-orange-900',
              innerBorder: 'border-orange-300'
            },
            red: {
              bg: 'bg-red-50',
              border: 'border-red-200',
              title: 'text-red-900',
              innerBorder: 'border-red-300'
            },
            purple: {
              bg: 'bg-purple-50',
              border: 'border-purple-200',
              title: 'text-purple-900',
              innerBorder: 'border-purple-300'
            },
            indigo: {
              bg: 'bg-indigo-50',
              border: 'border-indigo-200',
              title: 'text-indigo-900',
              innerBorder: 'border-indigo-300'
            },
            pink: {
              bg: 'bg-pink-50',
              border: 'border-pink-200',
              title: 'text-pink-900',
              innerBorder: 'border-pink-300'
            },
            teal: {
              bg: 'bg-teal-50',
              border: 'border-teal-200',
              title: 'text-blue-700',
              innerBorder: 'border-teal-300'
            },
            gold: {
              bg: 'bg-amber-50',
              border: 'border-amber-300',
              title: 'text-blue-700',
              innerBorder: 'border-amber-400'
            }
          }

          const colorScheme = colorSchemes[(item as any).color || 'green'] || colorSchemes.green
          const icon = (item as any).icon || '💡'

          return (
            <div key={index} className={`my-2 sm:my-4 p-2 sm:p-3 ${colorScheme.bg} border ${colorScheme.border} rounded`}>
              <h4 className={`font-bold ${colorScheme.title} mb-1 sm:mb-2 flex items-center text-xs sm:text-sm leading-tight`}>
                <span className="text-base sm:text-lg mr-1">{icon}</span>
                {item.title || 'Ví dụ minh họa'}
              </h4>
              <div className={`bg-white p-2 sm:p-3 rounded border ${colorScheme.innerBorder}`}>
                <div className="text-gray-800 leading-tight text-sm sm:text-base">
                  <span dangerouslySetInnerHTML={{ __html: processedExampleContent }} />
                </div>
              </div>
            </div>
          )

        case 'image':
          const ImageWithResolvedSrc: React.FC<{ item: ContentItem; index: number }> = ({ item, index }) => {
            const [resolvedSrc, setResolvedSrc] = useState<string>((item as any).src || (item.content as string) || '')
            const [loadError, setLoadError] = useState(false)

            useEffect(() => {
              const rawSrc = (item as any).src || (item.content as string) || ''
              console.log('🖼️ [Image] Processing image:', { rawSrc, startsWithHttp: rawSrc.startsWith('http') })

              if (rawSrc && !rawSrc.startsWith('data:') && !rawSrc.startsWith('blob:') && !rawSrc.startsWith('http')) {
                // Resolve teacher-content paths
                console.log('🔄 [Image] Resolving non-HTTP path from IndexedDB...')
                imageExtractor.resolveMediaSrc(rawSrc)
                  .then(resolved => {
                    console.log('✅ [Image] Resolved from IndexedDB:', resolved)
                    setResolvedSrc(resolved)
                  })
                  .catch(async (error) => {
                    console.warn('⚠️ Failed to resolve media src, trying IndexedDB fallback:', error)
                    // Try IndexedDB fallback
                    try {
                      const fallbackUrl = await imageExtractor.resolveMediaSrc(rawSrc)
                      console.log('✅ [Image] Fallback resolved:', fallbackUrl)
                      setResolvedSrc(fallbackUrl)
                    } catch (fallbackError) {
                      console.error('❌ IndexedDB fallback also failed:', fallbackError)
                      setResolvedSrc(rawSrc)
                    }
                  })
              } else if (rawSrc) {
                console.log('✅ [Image] Using direct URL (HTTP/data/blob):', rawSrc.substring(0, 100))
                setResolvedSrc(rawSrc)
              }
            }, [item])

            const handleImageError = async (e: React.SyntheticEvent<HTMLImageElement>) => {
              const target = e.target as HTMLImageElement
              const rawSrc = (item as any).src || (item.content as string) || ''

              console.error('❌ [Image] Failed to load:', {
                resolvedSrc,
                rawSrc,
                naturalWidth: target.naturalWidth,
                naturalHeight: target.naturalHeight,
                complete: target.complete
              })

              if (!loadError && rawSrc && !rawSrc.startsWith('data:') && !rawSrc.startsWith('blob:')) {
                console.warn('⚠️ Image failed to load from server, trying IndexedDB fallback:', resolvedSrc)
                setLoadError(true)

                // Try to load from IndexedDB
                try {
                  const fallbackUrl = await imageExtractor.resolveMediaSrc(rawSrc)
                  if (fallbackUrl && fallbackUrl.startsWith('blob:')) {
                    target.src = fallbackUrl
                    console.log('✅ Loaded image from IndexedDB fallback')
                    return
                  }
                } catch (error) {
                  console.error('❌ IndexedDB fallback failed:', error)
                }
              }

              // Final fallback: show error message
              console.error('❌ [Image] All attempts failed. Final URL:', resolvedSrc)
              target.alt = `❌ Không tải được: ${resolvedSrc}`
            }

            const handleImageLoad = () => {
              console.log('✅ [Image] Successfully loaded:', resolvedSrc.substring(0, 100))
            }

            return (
              <div key={index} className="my-2 sm:my-3 text-center">
                <img
                  src={resolvedSrc}
                  alt={item.alt || 'Hình ảnh minh họa'}
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                  style={{ maxHeight: '500px', objectFit: 'contain' }}
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                />
                {item.caption && (
                  <p className="text-xs sm:text-sm text-gray-600 mt-2 italic">{item.caption}</p>
                )}
              </div>
            )
          }
          return <ImageWithResolvedSrc item={item} index={index} />

        case 'video':
          const VideoWithResolvedSrc: React.FC<{ item: ContentItem; index: number }> = ({ item, index }) => {
            const rawVideoSrc = (item as any).src || (item.content as string) || (item as any).url || ''

            // Helper to resolve initial path synchronously
            const getInitialResolvedSrc = (src: string) => {
              if (!src) return ''

              // Handle static videos in public folder (allow both /videos/ and /Videos/)
              if (src.toLowerCase().startsWith('/videos/') || src.toLowerCase().startsWith('videos/')) {
                const cleanPath = src.startsWith('/') ? src : `/${src}`

                // Rewrite /videos/ or /Videos/ -> /bank/Videos/
                // This ensures we key into the /bank structure correctly
                const bankPath = cleanPath.replace(/^\/[vV]ideos\//, '/bank/Videos/')

                // Trong production (Vercel), dùng absolute URL tới server chính
                if (import.meta.env.PROD) {
                  // API_CONFIG.bank.baseUrl là https://vatli365.vn/bank
                  // bankPath là /bank/Videos/... -> Cần bỏ /bank ở đầu để ghép
                  const serverPath = bankPath.replace('/bank/', '/')
                  const fullUrl = `${API_CONFIG.bank.baseUrl}${serverPath}`
                  console.log(`🌍 [Video Init] Production: Rewriting path to server: ${src} → ${fullUrl}`)
                  return fullUrl
                }

                // Trong development, dùng local path
                // User logic: videos lưu trong public trên local
                console.log(`💻 [Video Init] Development: Using local public path: ${src} → ${cleanPath}`)
                return cleanPath
              }

              // Handle external URLs immediately
              if ((src.startsWith('http://') || src.startsWith('https://')) && !src.includes('/teacher-content/')) {
                return src
              }

              // Handle data/blob URLs
              if (src.startsWith('data:') || src.startsWith('blob:')) {
                return src
              }

              return src // Default to raw, will be resolved in useEffect if needed
            }

            const [resolvedVideoSrc, setResolvedVideoSrc] = useState<string>(getInitialResolvedSrc(rawVideoSrc))
            const [isVideoLoading, setIsVideoLoading] = useState(true)

            useEffect(() => {
              if (rawVideoSrc) {
                // Re-run resolution if rawVideoSrc changes
                const initialSrc = getInitialResolvedSrc(rawVideoSrc)
                if (initialSrc !== rawVideoSrc && initialSrc !== resolvedVideoSrc) {
                  setResolvedVideoSrc(initialSrc)
                  return
                }

                // Async resolution for teacher-content
                let pathToResolve = rawVideoSrc
                if ((rawVideoSrc.startsWith('http://') || rawVideoSrc.startsWith('https://')) && rawVideoSrc.includes('/teacher-content/')) {
                  const match = rawVideoSrc.match(/\/wp-content\/uploads\/(teacher-content\/.+)$/)
                  if (match) {
                    pathToResolve = `/${match[1]}`
                  }
                }

                // Only resolve if it's likely a teacher content path or needs resolution
                if (!getInitialResolvedSrc(rawVideoSrc).startsWith('http') && !rawVideoSrc.startsWith('/videos/') && !rawVideoSrc.startsWith('data:') && !rawVideoSrc.startsWith('blob:')) {
                  console.log(`🔄 [Phần 3 - Simulation] Resolving video path: ${pathToResolve}`)
                  imageExtractor.resolveMediaSrc(pathToResolve)
                    .then(async (resolved) => {
                      // Logic xử lý header như cũ...
                      if (resolved.includes('/teacher-content/media?path=')) {
                        try {
                          const headResponse = await fetch(resolved, { method: 'HEAD' })
                          const fullFilename = headResponse.headers.get('X-Full-Filename')
                          const fullPath = headResponse.headers.get('X-Full-Path')
                          if (fullFilename && fullPath) {
                            const API_BASE_URL = import.meta.env.VITE_WP_API_URL || 'https://vatli365.vn/wp-json/qoder/v1'
                            const updatedUrl = `${API_BASE_URL}/teacher-content/media?path=${encodeURIComponent(fullPath)}`
                            setResolvedVideoSrc(updatedUrl)
                            return
                          }
                        } catch (e) {
                          console.warn(`⚠️ Failed to get full filename:`, e)
                        }
                      }
                      setResolvedVideoSrc(resolved)
                    })
                    .catch((error) => {
                      console.warn(`⚠️ Failed to resolve path:`, error)
                      setResolvedVideoSrc(pathToResolve)
                    })
                }
              }
            }, [rawVideoSrc])

            const videoSrc = resolvedVideoSrc
            const isYouTube = (item as any).isYouTube ||
              (typeof videoSrc === 'string' &&
                (videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be')))

            if (isYouTube) {
              // Extract video ID for YouTube
              let videoId = (item as any).videoId || ''
              if (!videoId) {
                const youtubeMatch = videoSrc.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s\)]+)/)
                if (youtubeMatch) {
                  videoId = youtubeMatch[1]
                }
              }

              return (
                <div key={index} className="my-4 sm:my-6">
                  <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-4 sm:p-6 border-2 border-red-200">
                    {(item as any).title && (
                      <h4 className="text-lg font-bold text-gray-900 mb-2">
                        {(item as any).title}
                      </h4>
                    )}
                    {(item as any).description && (
                      <p className="text-sm text-gray-700 mb-3">
                        {(item as any).description}
                      </p>
                    )}
                    <div
                      className="youtube-container relative w-full"
                      style={{
                        paddingBottom: '56.25%',
                        height: 0,
                        overflow: 'hidden',
                        borderRadius: '0.5rem'
                      }}
                      data-video-id={videoId}
                      data-video-url={videoSrc}
                    >
                      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-900 rounded-lg">
                        <div className="text-center text-white p-4">
                          <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                          </svg>
                          <p className="text-sm">Đang tải video YouTube...</p>
                        </div>
                      </div>
                    </div>
                    {(item as any).caption && (
                      <p className="text-xs sm:text-sm text-gray-600 mt-3 italic text-center">
                        {(item as any).caption}
                      </p>
                    )}
                  </div>
                </div>
              )
            }

            // Regular video file
            return (
              <div key={index} className="my-4 sm:my-6">
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 sm:p-6 border-2 border-purple-200">
                  {(item as any).title && (
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {(item as any).title}
                    </h4>
                  )}
                  {(item as any).description && (
                    <p className="text-sm text-gray-700 mb-3">
                      {(item as any).description}
                    </p>
                  )}
                  {(
                    <video
                      controls
                      preload="metadata"
                      className="w-full rounded-lg shadow-lg"
                      style={{ maxHeight: '500px' }}
                      data-video-src={resolvedVideoSrc}
                      crossOrigin={resolvedVideoSrc.startsWith('http') ? "anonymous" : undefined}
                      onLoadStart={() => {
                        console.log(`🎬 [Video] Starting to load: ${resolvedVideoSrc}`)
                        setIsVideoLoading(true)
                      }}
                      onLoadedData={() => {
                        console.log(`✅ [Video] Loaded successfully: ${resolvedVideoSrc}`)
                        setIsVideoLoading(false)
                      }}
                      onError={(e) => {
                        const video = e.currentTarget
                        console.warn(`⚠️ [Video] Error loading: ${resolvedVideoSrc}`, {
                          networkState: video.networkState,
                          readyState: video.readyState
                        })
                        setIsVideoLoading(false)
                        // Auto-retry after 2s if it's a server URL
                        if (resolvedVideoSrc.startsWith('http')) {
                          setTimeout(() => {
                            console.log(`🔄 [Video] Auto-retrying: ${resolvedVideoSrc}`)
                            video.load()
                          }, 2000)
                        }
                      }}
                    >
                      <source src={resolvedVideoSrc} type="video/mp4" />
                      <source src={resolvedVideoSrc} type="video/webm" />
                      <source src={resolvedVideoSrc} type="video/ogg" />
                      Trình duyệt của bạn không hỗ trợ video.
                    </video>
                  )}
                  {isVideoLoading && (
                    <div className="text-center text-sm text-gray-500 mt-2">
                      ⏳ Đang tải video...
                    </div>
                  )}
                  {(item as any).caption && (
                    <p className="text-xs sm:text-sm text-gray-600 mt-3 italic text-center">
                      {(item as any).caption}
                    </p>
                  )}
                </div>
              </div>
            )
          }
          return <VideoWithResolvedSrc item={item} index={index} />

        case 'code':
          return (
            <div key={index} className="my-6">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{item.content as string}</code>
              </pre>
              {item.language && (
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {item.language}
                </div>
              )}
            </div>
          )

        case 'table':
          return (
            <div key={index} className="my-6 sm:my-8 flex justify-center">
              <div className="w-full sm:w-4/5 md:w-3/4 lg:w-2/3 xl:w-1/2">
                {item.caption && (
                  <div className="text-center mb-3">
                    <p className="text-base sm:text-lg font-semibold text-gray-700">{item.caption}</p>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table
                    className="w-full bg-white mx-auto"
                    style={{
                      borderCollapse: 'collapse',
                      border: '2px solid #999'
                    }}
                  >
                    {item.headers && (
                      <thead>
                        <tr>
                          {item.headers.map((header: string, headerIndex: number) => {
                            const processedHeader = processTextContent(header)
                            const isFirstColumn = headerIndex === 0
                            return (
                              <th
                                key={headerIndex}
                                className={`px-3 py-3 text-center text-sm font-bold ${isFirstColumn ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-900'}`}
                                style={{
                                  border: '1px solid #999',
                                  borderTop: '1px solid #999',
                                  borderBottom: '1px solid #999',
                                  borderLeft: '1px solid #999',
                                  borderRight: '1px solid #999'
                                }}
                              >
                                <span dangerouslySetInnerHTML={{ __html: processedHeader }} />
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                    )}
                    {item.rows && (
                      <tbody>
                        {item.rows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => {
                              const processedCell = processTextContent(cell)
                              const isFirstColumn = cellIndex === 0
                              return (
                                <td
                                  key={cellIndex}
                                  className={`px-3 py-3 text-center text-sm ${isFirstColumn ? 'bg-blue-50 font-semibold text-blue-900' : 'text-gray-800'}`}
                                  style={{
                                    border: '1px solid #999',
                                    borderTop: '1px solid #999',
                                    borderBottom: '1px solid #999',
                                    borderLeft: '1px solid #999',
                                    borderRight: '1px solid #999'
                                  }}
                                >
                                  <span dangerouslySetInnerHTML={{ __html: processedCell }} />
                                </td>
                              )
                            })}
                          </tr>
                        ))}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          )

        case 'quiz':
          return (
            <div key={index} className="my-6 p-6 bg-blue-50 border border-blue-200 rounded-lg group relative">
              <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                🎯 {item.title || 'Câu hỏi kiểm tra'}
              </h4>

              {item.timeLimit && (
                <div className="mb-4 text-sm text-blue-700">
                  ⏱️ Thời gian: {Math.floor(item.timeLimit / 60)} phút
                </div>
              )}

              <div className="space-y-4">
                {item.questions?.map((question: any, qIndex: number) => (
                  <div key={qIndex} className="bg-white p-4 rounded-lg border border-blue-200 group/question relative">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h5 className="font-medium text-blue-700 mb-2">
                          Câu {qIndex + 1}:
                        </h5>
                        <div className="text-gray-800">
                          <KaTeXRenderer content={question.question || question.cau_hoi || question.cauHoi || ''} />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {question.points || 1} điểm
                        </span>
                        {/* AIButton removed - questions already have explanations */}
                      </div>
                    </div>

                    {/* Multiple Choice */}
                    {question.type === 'multiple_choice' && question.options && (
                      <div className="space-y-2">
                        {question.options.map((option: string, optIndex: number) => (
                          <label key={optIndex} className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-blue-50">
                            <input
                              type="radio"
                              name={`quiz-${index}-question-${qIndex}`}
                              value={optIndex}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">
                              <KaTeXRenderer content={option} />
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* True/False */}
                    {question.type === 'true_false' && (
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-blue-50">
                          <input
                            type="radio"
                            name={`quiz-${index}-question-${qIndex}`}
                            value="true"
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">Đúng</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-blue-50">
                          <input
                            type="radio"
                            name={`quiz-${index}-question-${qIndex}`}
                            value="false"
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">Sai</span>
                        </label>
                      </div>
                    )}

                    {/* Fill in the blank */}
                    {question.type === 'fill_blank' && (
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder="Nhập câu trả lời..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {question.alternatives && question.alternatives.length > 0 && (
                          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                            <p className="text-sm text-yellow-800 font-medium mb-2">💡 Gợi ý:</p>
                            <p className="text-sm text-yellow-700">
                              Các từ khóa có thể dùng: {question.alternatives.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Explanation (hidden by default) */}
                    {question.explanation && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-700">
                          Xem giải thích
                        </summary>
                        <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-sm text-green-800">
                            <KaTeXRenderer content={question.explanation} />
                          </div>
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>

              {item.passingScore && (
                <div className="mt-4 text-sm text-blue-700">
                  📊 Điểm đạt: {item.passingScore}/{item.questions?.reduce((sum: number, q: any) => sum + (q.points || 1), 0) || 0}
                </div>
              )}

              <button className="btn-primary mt-4 px-6 py-2 rounded-lg">
                Nộp bài
              </button>
            </div>
          )

        default:
          // 'warning' (Lưu ý) — không nằm trong union type nên xử lý tại default cho gọn.
          if ((item as any).type === 'warning') {
            const warnContent = processTextContent(((item as any).content as string) || '')
            return (
              <div key={index} className="my-2 sm:my-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded-r">
                <h4 className="font-bold text-amber-800 mb-1 flex items-center text-sm sm:text-base">
                  <span className="mr-1.5">⚠️</span>{(item as any).title || 'Lưu ý'}
                </h4>
                <div className="text-gray-800 leading-snug text-sm sm:text-base">
                  <span dangerouslySetInnerHTML={{ __html: warnContent }} />
                </div>
              </div>
            )
          }
          // Graceful degradation cho unknown content types
          console.warn(`Unknown content type: ${item.type}`, item)

          // Thử render content dưới dạng text nếu có thể
          if (typeof item.content === 'string') {
            return (
              <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg mb-4">
                <p className="text-gray-700 text-sm">
                  {item.content}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  (Loại nội dung: {item.type})
                </p>
              </div>
            )
          }

          // Fallback cuối cùng
          return (
            <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
              <p className="text-blue-800 text-sm">
                📝 Nội dung đặc biệt (loại: {item.type})
              </p>
              <p className="text-blue-700 text-xs mt-1">
                Nội dung này có thể cần xử lý đặc biệt để hiển thị đúng.
              </p>
            </div>
          )
      }
    } catch (error) {
      const renderError = error as Error
      console.error('Lỗi render content item:', renderError, item)
      onError?.(renderError, item)

      return (
        <div key={index} className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
          <p className="text-red-800 text-sm font-medium mb-2">
            Lỗi hiển thị nội dung
          </p>
          <p className="text-red-700 text-xs">
            Loại: {item.type} | Lỗi: {renderError.message}
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-red-600 hover:text-red-700">
              Chi tiết nội dung
            </summary>
            <pre className="mt-1 text-xs text-red-600 bg-red-100 p-2 rounded overflow-auto">
              {JSON.stringify(item, null, 2)}
            </pre>
          </details>
        </div>
      )
    }
  }

  if (!content || !Array.isArray(content)) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-800 text-sm">
          Không có nội dung để hiển thị hoặc định dạng không đúng
        </p>
      </div>
    )
  }

  if (content.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <p className="text-gray-600">Chưa có nội dung</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {content.map((item, index) => renderContentItem(item, index))}
    </div>
  )
}

export default ContentRenderer
